import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  NoteAdd as NoteAddIcon,
  Event as EventIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTradeNotes,
  createTradeNote,
  updateTradeNote,
  deleteTradeNote,
} from './tradeNotesSlice';
import { eventsAPI } from '../../api/events';
import { DWLF_EVENT_TYPES, getEventTypeLabel } from '../../constants/eventTypes';
import DWLFLoader from '../../components/DWLFLoader';

const formatTimestamp = (value) => {
  if (!value) { return ''; }
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const parseEventLabel = (eventId) => {
  if (!eventId) { return ''; }
  const parts = eventId.split('_');
  if (parts.length < 2) {
    return eventId;
  }
  const symbol = parts[0];
  const date = parts[parts.length - 1];
  const rawType = parts.slice(1, -1).join('_') || 'event';
  const formattedDate = (() => {
    try {
      return new Date(date).toLocaleDateString();
    } catch (error) {
      return date;
    }
  })();
  const typeLabel = getEventTypeLabel(rawType);
  return `${symbol} · ${typeLabel} · ${formattedDate}`;
};

const TradeNotesSection = ({ tradeId, linkedEvents = [], symbol, entryAt }) => {
  const dispatch = useDispatch();
  const [noteText, setNoteText] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editEvents, setEditEvents] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [availableEventsLoading, setAvailableEventsLoading] = useState(false);
  const [availableEventsError, setAvailableEventsError] = useState(null);
  const [notePendingDelete, setNotePendingDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const notesEntry = useSelector((state) => state.tradeNotes.notesByTrade[tradeId] || { items: [], cursor: null });
  const loading = useSelector((state) => state.tradeNotes.loadingByTrade[tradeId]);
  const loadingMore = useSelector((state) => state.tradeNotes.loadingMoreByTrade[tradeId]);
  const loadError = useSelector((state) => state.tradeNotes.errorByTrade[tradeId]);
  const creating = useSelector((state) => state.tradeNotes.creating);
  const createError = useSelector((state) => state.tradeNotes.createError);
  const updatingByNote = useSelector((state) => state.tradeNotes.updatingByNote);
  const deletingByNote = useSelector((state) => state.tradeNotes.deletingByNote);
  const updateErrors = useSelector((state) => state.tradeNotes.updateErrorByNote);
  const deleteErrors = useSelector((state) => state.tradeNotes.deleteErrorByNote);

  useEffect(() => {
    if (tradeId) {
      dispatch(fetchTradeNotes({ tradeId }));
    }
  }, [dispatch, tradeId]);

  useEffect(() => {
    setSelectedEvents([]);
    setEditingNoteId(null);
    setEditText('');
    setEditEvents([]);
    setNoteText('');
    setNotePendingDelete(null);
    setDeleteDialogOpen(false);
  }, [tradeId]);

  useEffect(() => {
    if (!symbol || !entryAt) {
      setAvailableEvents([]);
      setAvailableEventsError(null);
      return;
    }

    let isCancelled = false;
    const entryDate = new Date(entryAt);
    if (Number.isNaN(entryDate.getTime())) {
      setAvailableEvents([]);
      setAvailableEventsError('Invalid entry date');
      return;
    }

    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const diffMs = Date.now() - entryDate.getTime();
    const diffDays = Math.max(1, Math.ceil(diffMs / millisecondsPerDay));
    const lookbackDays = Math.min(Math.max(diffDays + 2, 7), 365);

    const fetchEvents = async () => {
      setAvailableEventsLoading(true);
      setAvailableEventsError(null);

      try {
        const results = await Promise.all(
          DWLF_EVENT_TYPES.map(async (eventType) => {
            try {
              const response = await eventsAPI.getRecentEvents({ type: eventType, days: lookbackDays });
              return response.events || [];
            } catch (error) {
              console.error('Failed to fetch events', eventType, error);
              return [];
            }
          })
        );

        if (isCancelled) { return; }

        const symbolUpper = symbol.toUpperCase();
        const filtered = [];

        results.flat().forEach((event) => {
          if (!event || !event.symbol || !event.eventType || !event.date) {
            return;
          }
          if (event.symbol.toUpperCase() !== symbolUpper) {
            return;
          }
          const eventDate = new Date(event.date);
          if (Number.isNaN(eventDate.getTime()) || eventDate < entryDate) {
            return;
          }
          filtered.push(event);
        });

        filtered.sort((a, b) => {
          const aTime = a?.eventTimestamp ? Date.parse(a.eventTimestamp) : Date.parse(a?.date || '') || 0;
          const bTime = b?.eventTimestamp ? Date.parse(b.eventTimestamp) : Date.parse(b?.date || '') || 0;
          return bTime - aTime;
        });

        setAvailableEvents(filtered);
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to load events for trade notes', error);
          setAvailableEventsError('Unable to load DWLF events for this trade');
          setAvailableEvents([]);
        }
      } finally {
        if (!isCancelled) {
          setAvailableEventsLoading(false);
        }
      }
    };

    fetchEvents();

    return () => {
      isCancelled = true;
    };
  }, [symbol, entryAt]);

  const eventOptions = useMemo(() => {
    const existing = linkedEvents || [];
    const fromNotes = (notesEntry.items || [])
      .flatMap((note) => (Array.isArray(note.linkedEventIds) ? note.linkedEventIds : []))
      .filter(Boolean);
    const fromAvailable = availableEvents.map((event) => `${event.symbol}_${event.eventType}_${event.date}`);
    return Array.from(new Set([...existing, ...fromNotes, ...fromAvailable]));
  }, [linkedEvents, notesEntry.items, availableEvents]);

  const handleCreate = async () => {
    const trimmedText = noteText.trim();
    if (!trimmedText) {
      return;
    }
    const payload = {
      text: trimmedText,
      linkedEventIds: selectedEvents.map((id) => id.trim()).filter(Boolean),
    };
    try {
      await dispatch(createTradeNote({ tradeId, payload })).unwrap();
      setNoteText('');
      setSelectedEvents([]);
    } catch (error) {
      // errors already captured in slice; no-op
    }
  };

  const startEdit = (note) => {
    setEditingNoteId(note.noteId);
    setEditText(note.text || '');
    setEditEvents(Array.isArray(note.linkedEventIds) ? note.linkedEventIds : []);
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditText('');
    setEditEvents([]);
  };

  const handleUpdate = async () => {
    if (!editingNoteId) { return; }
    const trimmed = editText.trim();
    if (!trimmed) {
      return;
    }
    const payload = {
      text: trimmed,
      linkedEventIds: editEvents.map((id) => id.trim()).filter(Boolean),
    };
    try {
      await dispatch(updateTradeNote({ tradeId, noteId: editingNoteId, payload })).unwrap();
      cancelEdit();
    } catch (error) {
      // handled via slice error state
    }
  };

  const requestDelete = (note) => {
    setNotePendingDelete(note);
    setDeleteDialogOpen(true);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setNotePendingDelete(null);
  };

  const confirmDelete = async () => {
    if (!notePendingDelete) {
      return;
    }
    try {
      await dispatch(deleteTradeNote({ tradeId, noteId: notePendingDelete.noteId })).unwrap();
      cancelDelete();
    } catch (error) {
      // handled via slice error state
    }
  };

  const renderEventChips = (events = []) => {
    if (!Array.isArray(events) || events.length === 0) {
      return null;
    }
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        {events.map((eventId) => (
          <Chip
            key={eventId}
            icon={<EventIcon fontSize="small" />}
            label={parseEventLabel(eventId)}
            size="small"
            variant="outlined"
          />
        ))}
      </Stack>
    );
  };

  const isEditing = Boolean(editingNoteId);

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Trade Notes
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Box sx={{
        backgroundColor: 'background.surface',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        p: 2,
        mb: 3,
      }}>
        <Stack spacing={2}>
          <TextField
            label="Add a note"
            placeholder="Capture what happened, why it matters, or how you plan to act."
            multiline
            minRows={3}
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
            disabled={creating || loading}
          />

          <Autocomplete
            multiple
            freeSolo
            options={eventOptions}
            value={selectedEvents}
            onChange={(_event, value) => setSelectedEvents(value)}
            loading={availableEventsLoading}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  variant="outlined"
                  icon={<EventIcon fontSize="small" />}
                  label={parseEventLabel(option)}
                />
              ))
            }
            loadingText="Loading DWLF events..."
            noOptionsText={availableEventsLoading ? 'Loading DWLF events...' : 'Type an event ID'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Link DWLF events (optional)"
                placeholder={eventOptions.length ? 'Select existing or type an event ID' : 'Type an event ID'}
              />
            )}
          />

          {availableEventsError && (
            <Alert severity="warning">{availableEventsError}</Alert>
          )}

          {createError && (
            <Alert severity="error">{createError}</Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="contained"
              startIcon={<NoteAddIcon />}
              onClick={handleCreate}
              disabled={creating || loading || !noteText.trim()}
            >
              Add Note
            </Button>
          </Box>
        </Stack>
      </Box>

      {loading && notesEntry.items.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <DWLFLoader size="medium" />
        </Box>
      ) : (
        <Stack spacing={2}>
          {notesEntry.items.length === 0 && !loadError && (
            <Typography variant="body2" color="text.secondary">
              No notes yet. Capture insights to build your trade journal.
            </Typography>
          )}

          {notesEntry.items.map((note) => {
            const updating = Boolean(updatingByNote[note.noteId]);
            const deleting = Boolean(deletingByNote[note.noteId]);
            const updateError = updateErrors[note.noteId];
            const deleteError = deleteErrors[note.noteId];
            const isCurrentEdit = editingNoteId === note.noteId;

            return (
              <Box
                key={note.noteId}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: 'background.surface',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                  <Box flex={1}>
                    {isCurrentEdit ? (
                      <Stack spacing={2}>
                        <TextField
                          label="Edit note"
                          multiline
                          minRows={3}
                          value={editText}
                          onChange={(event) => setEditText(event.target.value)}
                          disabled={updating}
                        />
                        <Autocomplete
                          multiple
                          freeSolo
                          options={eventOptions}
                          value={editEvents}
                          onChange={(_event, value) => setEditEvents(value)}
                          loading={availableEventsLoading}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                variant="outlined"
                                icon={<EventIcon fontSize="small" />}
                                label={parseEventLabel(option)}
                              />
                            ))
                          }
                          loadingText="Loading DWLF events..."
                          noOptionsText={availableEventsLoading ? 'Loading DWLF events...' : 'Type an event ID'}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Linked DWLF events"
                            />
                          )}
                        />
                      </Stack>
                    ) : (
                      <>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {note.text}
                        </Typography>
                        {renderEventChips(note.linkedEventIds)}
                      </>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {isCurrentEdit ? (
                      <>
                        <Tooltip title="Save">
                          <span>
                            <IconButton
                              color="primary"
                              onClick={handleUpdate}
                              disabled={updating || !editText.trim()}
                            >
                              <SaveIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton color="inherit" onClick={cancelEdit} disabled={updating}>
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit note">
                          <IconButton onClick={() => startEdit(note)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete note">
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => requestDelete(note)}
                              disabled={deleting}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="caption" color="text.secondary">
                  Created: {formatTimestamp(note.createdAt)}
                  {note.updatedAt && note.updatedAt !== note.createdAt && (
                    <>
                      {' · Updated: '}
                      {formatTimestamp(note.updatedAt)}
                    </>
                  )}
                </Typography>

                {updateError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {updateError}
                  </Alert>
                )}

                {deleteError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {deleteError}
                  </Alert>
                )}
              </Box>
            );
          })}

          {notesEntry.cursor && (
            <Box display="flex" justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => dispatch(fetchTradeNotes({ tradeId, cursor: notesEntry.cursor, append: true }))}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more notes'}
              </Button>
            </Box>
          )}
        </Stack>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-trade-note-title"
      >
        <DialogTitle id="delete-trade-note-title">Delete note?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove the selected note from the trade journal.
          </DialogContentText>
          {notePendingDelete?.text && (
            <Box mt={2} p={2} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.default' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {notePendingDelete.text}
              </Typography>
            </Box>
          )}
          {notePendingDelete && deleteErrors[notePendingDelete.noteId] && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteErrors[notePendingDelete.noteId]}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={notePendingDelete ? Boolean(deletingByNote[notePendingDelete.noteId]) : false}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={notePendingDelete ? Boolean(deletingByNote[notePendingDelete.noteId]) : false}
          >
            {notePendingDelete && deletingByNote[notePendingDelete.noteId] ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TradeNotesSection;
